import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Divider } from '@retailer-platform/shared-components/src/tds'
import { LegacySwitchBase as SwitchBase, IconButton } from '@instacart/ids-customers'
import { startCase } from 'lodash'
import styled from '@emotion/styled'
import { Text, NewSelect, spacing, Tooltip } from '@retailer-platform/shared-components'
import { HelpIcon, RefreshIcon, SearchIcon } from '@instacart/ids-core'
import {
  Input,
  PropertyContainer,
  PropertyRow,
  PropertyTitle,
  SectionHeader,
  FullWidthProperty,
  FlexOne,
} from '../../shared-styled-components/SharedStyledComponents'
import { useDomainMessages } from '../../../utils/domain/intl'
import { useListAuditWorkflowsQuery, useGetAuditWorkflowConfigsQuery } from '../../../api'
import {
  QAS_CLIENT,
  type QualityAuditWorkflowColumnConfig,
} from '../../../types/qualityAuditWorkflows.types'
import { useOutsourceVendor } from '../../../utils/qas/useOutsourceVendor'
import { type FormProps } from './StepAttributeForm'

const OutputGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  width: '100%',
  gap: spacing.X8,
})

const SpacerDiv = styled.div({
  marginRight: spacing.X16,
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.X8,
})

const GridCell = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
})

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr auto auto',
  columnGap: spacing.X28,
  rowGap: spacing.X16,
  width: '100%',
  padding: spacing.X16,
})

const WorkflowContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X8,
  flex: 1,
})

// Utility function to extract attributes from workflow config
const extractAttributesFromConfig = (configData: any): string[] => {
  try {
    const configs = configData?.demeterServiceGetAuditWorkflowConfigs?.configs ?? []
    const columnConfig: QualityAuditWorkflowColumnConfig[] =
      configs.length > 0 ? JSON.parse(configs[0].configValue ?? '[]') : []

    const attributeNames = new Set<string>()
    columnConfig.forEach(config => {
      config.inputs?.forEach(input => {
        if (
          (!input.group_name || input.group_name.length === 0) &&
          input.name &&
          input.type?.toLowerCase() === 'attribute'
        ) {
          attributeNames.add(input.name)
        }
      })
    })

    return Array.from(attributeNames)
  } catch (e) {
    return []
  }
}

// Syncs existing components with updated workflow attributes
const syncComponentsWithWorkflow = (
  existingComponents: ComponentProps[],
  latestAttributes: string[],
  qasWorkflow: string
): ComponentProps[] => {
  const latestAttributesSet = new Set(latestAttributes)
  const result: ComponentProps[] = []

  existingComponents.forEach(component => {
    if (latestAttributesSet.has(component.name)) {
      result.push({
        ...component,
        _workflowKey: `${qasWorkflow}_${component.name}`,
      })
      latestAttributesSet.delete(component.name)
    }
  })

  latestAttributesSet.forEach(attributeName => {
    result.push({
      name: attributeName,
      campField: '',
      applySuggestedValues: false,
      updateOriginalSource: false,
      _workflowKey: `${qasWorkflow}_${attributeName}`,
    })
  })

  return result
}
// Custom hook to handle workflow configuration logic
const useWorkflowConfig = (qasWorkflow: string) => {
  // Parse workflow identifiers
  const workflowIdentifiers = useMemo(() => {
    if (!qasWorkflow) return { workflowL1: '', workflowL2: '', workflowL3: '' }
    const parts = qasWorkflow.split('/')
    return {
      workflowL1: parts.length >= 1 ? parts[0] : '',
      workflowL2: parts.length >= 2 ? parts[1] : '',
      workflowL3: parts.length >= 3 ? parts[2] : '',
    }
  }, [qasWorkflow])

  // Fetch workflow configs
  const {
    data: workflowConfigsData,
    loading: workflowConfigsLoading,
    refetch,
  } = useGetAuditWorkflowConfigsQuery({
    variables: {
      client: QAS_CLIENT,
      configType: 'columns',
      ...workflowIdentifiers,
    },
    skip: !qasWorkflow || !workflowIdentifiers.workflowL1,
  })

  // Extract available attributes
  const availableAttributes = useMemo(
    () => extractAttributesFromConfig(workflowConfigsData),
    [workflowConfigsData]
  )

  return {
    availableAttributes,
    workflowConfigsLoading,
    refetchWorkflowConfigs: refetch,
  }
}

interface ComponentProps {
  name: string
  campField: string
  applySuggestedValues: boolean
  updateOriginalSource: boolean
  _workflowKey?: string
}

interface ComponentRowProps {
  component: ComponentProps
  onChange: (component: ComponentProps) => void
  disabled?: boolean
}

const ComponentRow: React.FC<ComponentRowProps> = ({ component, onChange, disabled }) => {
  const [localComponent, setLocalComponent] = useState(component)

  // Sync local state when component prop changes
  useEffect(() => {
    setLocalComponent(component)
  }, [component])

  // Auto-save function that cleans the component and calls onChange
  const handleAutoSave = (updatedComponent: ComponentProps) => {
    const cleanedComponent = { ...updatedComponent }
    delete cleanedComponent._workflowKey
    onChange(cleanedComponent)
  }

  const i18n = useDomainMessages({
    applySuggestedValuesHelp: 'catalogAdminDomain.qas.form.label.applySuggestedValuesHelp',
    updateOriginalSourceHelp: 'catalogAdminDomain.qas.form.label.updateOriginalSourceHelp',
  })
  return (
    <React.Fragment>
      <GridCell>
        <Text>{localComponent.name}</Text>
      </GridCell>
      <GridCell>
        {disabled ? (
          <Text>{localComponent.campField || ''}</Text>
        ) : (
          <Input
            type="text"
            error={!localComponent.campField}
            value={localComponent.campField}
            onChange={event => {
              const updatedComponent = { ...localComponent, campField: event.target.value }
              setLocalComponent(updatedComponent)
            }}
            onBlur={() => handleAutoSave(localComponent)}
          />
        )}
      </GridCell>
      <GridCell>
        <SwitchBase
          checked={localComponent.applySuggestedValues}
          disabled={disabled}
          description={i18n.applySuggestedValuesHelp}
          onChange={event => {
            const updatedComponent = {
              ...localComponent,
              applySuggestedValues: event.target.checked,
            }
            setLocalComponent(updatedComponent)
            handleAutoSave(updatedComponent)
          }}
        />
      </GridCell>
      <GridCell>
        <SwitchBase
          checked={localComponent.updateOriginalSource}
          disabled={disabled || !localComponent.applySuggestedValues}
          description={i18n.updateOriginalSourceHelp}
          onChange={event => {
            const updatedComponent = {
              ...localComponent,
              updateOriginalSource: event.target.checked,
            }
            setLocalComponent(updatedComponent)
            handleAutoSave(updatedComponent)
          }}
        />
      </GridCell>
    </React.Fragment>
  )
}

const HeaderRow = () => {
  const i18n = useDomainMessages({
    name: 'catalogAdminDomain.qas.list.columns.name',
    campField: 'catalogAdminDomain.qas.list.columns.campField',
    applySuggestedValues: 'catalogAdminDomain.qas.list.columns.applySuggestedValues',
    updateOriginalSource: 'catalogAdminDomain.qas.list.columns.updateOriginalSource',
    nameHelp: 'catalogAdminDomain.qas.list.columns.name.help',
    campFieldHelp: 'catalogAdminDomain.qas.list.columns.campField.help',
    applySuggestedValuesHelp: 'catalogAdminDomain.qas.list.columns.applySuggestedValues.help',
    updateOriginalSourceHelp: 'catalogAdminDomain.qas.list.columns.updateOriginalSource.help',
  })
  return (
    <React.Fragment>
      <PropertyTitle>
        QAS Attribute
        <Tooltip placement="bottom" target={<HelpIcon />}>
          {i18n.nameHelp}
        </Tooltip>
      </PropertyTitle>
      <PropertyTitle>
        {i18n.campField}
        <Tooltip placement="bottom" target={<HelpIcon />}>
          {i18n.campFieldHelp}
        </Tooltip>
      </PropertyTitle>
      <PropertyTitle>
        {i18n.applySuggestedValues}
        <Tooltip placement="bottom" target={<HelpIcon />}>
          {i18n.applySuggestedValuesHelp}
        </Tooltip>
      </PropertyTitle>
      <PropertyTitle>
        {i18n.updateOriginalSource}
        <Tooltip placement="bottom" target={<HelpIcon />}>
          {i18n.updateOriginalSourceHelp}
        </Tooltip>
      </PropertyTitle>
    </React.Fragment>
  )
}

const QASForm: React.FC<FormProps> = ({ attributes, mode, onChange, version }) => {
  const i18n = useDomainMessages({
    qasAuditDetails: 'catalogAdminDomain.qas.form.header.qasAuditDetails',
    qasWorkflow: 'catalogAdminDomain.qas.form.label.qasWorkflow',
    qasWorkflowHelp: 'catalogAdminDomain.qas.form.label.qasWorkflow.help',
    qasWorkflowPlaceholder: 'catalogAdminDomain.qas.form.label.qasWorkflow.placeholder',
    reuse: 'catalogAdminDomain.qas.form.label.reuse',
    reuseHelp: 'catalogAdminDomain.qas.form.label.reuse.help',
    reuseSinceVersion: 'catalogAdminDomain.qas.form.label.reuseSinceVersion',
    reuseSinceVersionHelp: 'catalogAdminDomain.qas.form.label.reuseSinceVersion.help',
    outputFields: 'catalogAdminDomain.qas.form.label.outputFields',
    outputFieldsHelp: 'catalogAdminDomain.qas.form.label.outputFields.help',
    auditSuffix: 'catalogAdminDomain.qas.form.label.outputFields.auditResult.suffix',
    auditResultExplanation:
      'catalogAdminDomain.qas.form.label.outputFields.auditResult.explanation',
    componentExplanation: 'catalogAdminDomain.qas.form.label.outputFields.component.explanation',
    outputFieldsComponentTaskContextField:
      'catalogAdminDomain.qas.form.label.outputFields.component.taskContextField',
    outputFieldsComponentTaskContextFieldExplanation:
      'catalogAdminDomain.qas.form.label.outputFields.component.taskContextField.explanation',
  })

  // Track the last workflow we auto-populated for to avoid duplicate runs
  const lastAutoPopulatedWorkflow = useRef<string>('')

  // Initialize tracking on mount if components already exist (preserve on new version creation)
  useEffect(() => {
    if (attributes.qasWorkflow && attributes.components.length > 0) {
      lastAutoPopulatedWorkflow.current = attributes.qasWorkflow
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If the user contains an outsource permission include it in the request.
  const outsourceVendor = useOutsourceVendor()

  const { data: apiListAuditWorkflows, ...listAuditWorkflowsQueryDataRest } =
    useListAuditWorkflowsQuery({
      variables: {
        client: QAS_CLIENT,
        returnTaskInformation: false,
        outsourceVendor,
      },
    })

  const workflows = useMemo(
    () => apiListAuditWorkflows?.demeterServiceListAuditWorkflows?.auditWorkflows ?? [],
    [apiListAuditWorkflows?.demeterServiceListAuditWorkflows?.auditWorkflows]
  )

  const workFlowOptions = useMemo(
    () =>
      workflows
        .map(workflow => ({
          label: (workflow.name ?? '')
            .split('/')
            .map(text => startCase(text))
            .join('/'),
          value: workflow.name ?? '',
        }))
        .sort((workflowA, workflowB) =>
          (workflowA?.value ?? '').localeCompare(workflowB?.value ?? '')
        ),
    [workflows]
  )

  const { availableAttributes, workflowConfigsLoading, refetchWorkflowConfigs } = useWorkflowConfig(
    attributes.qasWorkflow
  )

  const memoizedNewComponents = useMemo(() => {
    if (!attributes.qasWorkflow) {
      return []
    }

    return availableAttributes.map(attributeName => ({
      name: attributeName,
      campField: '',
      applySuggestedValues: false,
      updateOriginalSource: false,
      // Add workflow-specific key to force React to create fresh components
      _workflowKey: `${attributes.qasWorkflow}_${attributeName}`,
    }))
  }, [availableAttributes, attributes.qasWorkflow])

  useEffect(() => {
    if (
      mode !== 'edit' ||
      !attributes.qasWorkflow ||
      workflowConfigsLoading ||
      lastAutoPopulatedWorkflow.current === attributes.qasWorkflow
    ) {
      return
    }

    onChange({ ...attributes, components: memoizedNewComponents })
    lastAutoPopulatedWorkflow.current = attributes.qasWorkflow
  }, [attributes, onChange, memoizedNewComponents, workflowConfigsLoading, mode])

  const handleRefreshAttributes = async () => {
    if (mode !== 'edit' || !attributes.qasWorkflow || workflowConfigsLoading) return

    const result = await refetchWorkflowConfigs()
    const latestAttributes = extractAttributesFromConfig(result.data)
    const syncedComponents = syncComponentsWithWorkflow(
      attributes.components,
      latestAttributes,
      attributes.qasWorkflow
    )
    onChange({ ...attributes, components: syncedComponents })
    lastAutoPopulatedWorkflow.current = attributes.qasWorkflow
  }

  return (
    <>
      <SectionHeader>{i18n.qasAuditDetails}</SectionHeader>
      <PropertyContainer>
        <FullWidthProperty>
          <PropertyTitle>{i18n.qasWorkflow}</PropertyTitle>
          {mode !== 'edit' ? (
            <FlexOne>{attributes.qasWorkflow}</FlexOne>
          ) : (
            <WorkflowContainer>
              <NewSelect
                icon={SearchIcon}
                placeholder={i18n.qasWorkflowPlaceholder}
                onChange={value => {
                  if (mode === 'edit') {
                    onChange({ ...attributes, qasWorkflow: value })
                  }
                }}
                options={workFlowOptions}
                value={attributes.qasWorkflow}
                isLoading={listAuditWorkflowsQueryDataRest?.loading}
                isSearchable={true}
                styles={{
                  container: provided => ({
                    ...provided,
                    flex: 1,
                    minWidth: '300px', // Ensure minimum width for dropdown
                  }),
                }}
              />
              <IconButton
                icon={RefreshIcon}
                disabled={!attributes.qasWorkflow || workflowConfigsLoading}
                onPress={handleRefreshAttributes}
                accessibleLabel="Refresh QAS Attributes"
              />
            </WorkflowContainer>
          )}
        </FullWidthProperty>
      </PropertyContainer>

      <PropertyContainer>
        <PropertyRow>
          <SpacerDiv>
            <PropertyTitle>
              {i18n.reuse}
              <Tooltip placement="bottom" target={<HelpIcon />}>
                {i18n.reuseHelp}
              </Tooltip>
            </PropertyTitle>
            <SwitchBase
              checked={attributes.reuse}
              disabled={mode === 'view'}
              description="Toggle to enable results from this audit to be reused when the same entity_id and entity_type are seen."
              onChange={event => onChange({ ...attributes, reuse: event.target.checked })}
            />
          </SpacerDiv>
          <PropertyTitle>
            {i18n.reuseSinceVersion}
            <Tooltip placement="bottom" target={<HelpIcon />}>
              {i18n.reuseSinceVersionHelp}
            </Tooltip>
            :
          </PropertyTitle>
          <Input
            type="number"
            value={attributes.reuse ? attributes.reuseSince : ''}
            disabled={mode === 'view' || !attributes.reuse}
            onChange={event =>
              onChange({
                ...attributes,
                reuseSince: Math.max(Math.min(Number(event.target.value), version || 1), 1),
              })
            }
          />
        </PropertyRow>
      </PropertyContainer>
      <Divider />
      <SectionHeader>Components</SectionHeader>
      <Grid>
        <HeaderRow />
        {attributes.components.map((component: any) => (
          <ComponentRow
            key={component._workflowKey || component.name}
            disabled={mode !== 'edit'}
            component={component}
            onChange={component => {
              const cleanedComponent = { ...component }
              delete cleanedComponent._workflowKey
              onChange({
                ...attributes,
                components: attributes.components.map((c: any) =>
                  c.name === component.name ? cleanedComponent : c
                ),
              })
            }}
          />
        ))}
      </Grid>
      <Divider />
      <SectionHeader>{i18n.outputFields}</SectionHeader>
      <PropertyRow>
        <Text weight="regular" size="small">
          {i18n.outputFieldsHelp}
        </Text>
      </PropertyRow>
      <PropertyRow style={{ paddingTop: spacing.X16 }}>
        <OutputGrid>
          <Text weight="bold" size="small">
            {i18n.outputFieldsComponentTaskContextField}
          </Text>
          <Text weight="bold" size="small">
            {i18n.outputFieldsComponentTaskContextFieldExplanation}
          </Text>
          <Text weight="semibold" size="small">
            {attributes.qasWorkflow.split('/')[2]}
            {i18n.auditSuffix}:
          </Text>
          <Text weight="regular" size="small">
            {i18n.auditResultExplanation}
          </Text>
          {attributes.components
            .filter((component: any) => component.applySuggestedValues)
            .map((component: any) => (
              <React.Fragment key={component.name}>
                <Text weight="semibold" size="small">
                  {component.campField}:
                </Text>
                <Text weight="regular" size="small">
                  {i18n.componentExplanation}
                  {component.name}
                </Text>
                <Text weight="semibold" size="small">
                  {attributes.qasWorkflow.split('/')[2]}-{component.campField}-auditor-suggested:
                </Text>
                <Text weight="regular" size="small">
                  Indicates if a value was suggested by the auditor in the QAS audit. True if there
                  was a suggestion, false otherwise.
                </Text>
              </React.Fragment>
            ))}
        </OutputGrid>
      </PropertyRow>
    </>
  )
}

export default QASForm
